<template>
    <v-app id="app_approved_reversals">
        <v-container fluid>
            <v-row class="pl-2 pr-6 align-center">
                <v-col cols="12" md="7" class="mt-3">
                    <p class="custom_page_main_title">APPROVED PAYMENT REVERSALS</p>
                    <p class="custom_page_main_sub_title">View feedback for approved reversal requests.</p>
                </v-col>
                <v-col cols="12" md="5" class="px-4 mt-5">
                    <v-row class="custom_tool_bar_style justify-end">
                        <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
                        <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
                            <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
                        </v-btn>
                    </v-row>
                </v-col>
            </v-row>

            <v-card class="custom_card_box mt-8" elevation="1" border="sm opacity-1" rounded="lg">
                <v-card-text class="custom_tool_bar_style">
                    <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF">
                        <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
                    </v-btn>
                    <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel">
                        <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
                    </v-btn>
                </v-card-text>

                <v-divider thickness="2"></v-divider>

                <v-expansion-panels v-model="panelOpen" multiple>
                    <v-expansion-panel value="filters" elevation="0">
                        <v-expansion-panel-title expand-icon="mdi-menu-down">
                            <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
                            <span class="text-body-1 font-weight-bold">FILTERS</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row class="mt-2" dense>
                                <!-- Date Range -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-date-input
                                        v-model="dates"
                                        prepend-inner-icon="$calendar"
                                        prepend-icon=""
                                        label="Select Date Range (Approved Date)"
                                        multiple="range"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                        variant="outlined"
                                        clearable
                                        @update:model-value="onDateChange"
                                    ></v-date-input>
                                </v-col>

                                <!-- Area -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete
                                        v-model="selectDistrict"
                                        :items="areaLists"
                                        item-title="text"
                                        item-value="value"
                                        label="Area"
                                        placeholder="Select area"
                                        variant="outlined"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                        multiple
                                        clearable
                                    >
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip
                                                v-bind="props"
                                                color="#e7e7e7"
                                                variant="flat"
                                                class="text-black font-weight-medium"
                                                size="small"
                                                closable
                                            >
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Sales Rep -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete
                                        v-model="selectedAgent"
                                        :items="agentListsWithDetails"
                                        item-title="text"
                                        item-value="value"
                                        label="Sales Rep"
                                        placeholder="Select sales representative"
                                        variant="outlined"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                        multiple
                                        clearable
                                        :loading="salesRepsLoading"
                                    >
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="undefined">
                                                <template v-slot:prepend>
                                                    <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                                                </template>
                                                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                                                <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                                                    REG NO: {{ item.raw.registration }}
                                                </v-list-item-subtitle>
                                            </v-list-item>
                                        </template>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip
                                                v-bind="props"
                                                color="#e7e7e7"
                                                variant="flat"
                                                class="text-black font-weight-medium"
                                                size="small"
                                                closable
                                                close-icon="mdi-close-circle"
                                            >
                                                {{ item.raw.text }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Route -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete
                                        v-model="selectedRoute"
                                        :items="routeTypeOptions"
                                        item-title="text"
                                        item-value="value"
                                        label="Route"
                                        placeholder="Select route"
                                        variant="outlined"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                        multiple
                                        clearable
                                    >
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip
                                                v-bind="props"
                                                color="#e7e7e7"
                                                variant="flat"
                                                class="text-black font-weight-medium"
                                                size="small"
                                                closable
                                            >
                                                {{ item.title || item.text }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Collection Type -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete
                                        v-model="selectedCollectionType"
                                        :items="collectionTypeOptions"
                                        item-title="text"
                                        item-value="value"
                                        label="Collection Type"
                                        placeholder="Select type"
                                        variant="outlined"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                        multiple
                                        clearable
                                    >
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip
                                                v-bind="props"
                                                color="#e7e7e7"
                                                variant="flat"
                                                class="text-black font-weight-medium"
                                                size="small"
                                                closable
                                            >
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Search -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="search"
                                        clearable
                                        label="Search"
                                        placeholder="Search by Dealer, Receipt ID, or Reason"
                                        variant="outlined"
                                        density="comfortable"
                                        class="custom_input_field_color"
                                    ></v-text-field>
                                </v-col>
                            </v-row>

                            <v-divider class="mb-3 mt-2"></v-divider>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>

                <v-card-text>
                    <!-- Loading Indicator -->
                    <div v-if="loading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="#9C27B0" size="64"></v-progress-circular>
                        <p class="mt-4 text-grey">Loading approved reversals...</p>
                    </div>

                    <!-- No Data Message -->
                    <div v-else-if="filteredReversals.length === 0" class="text-center pa-8">
                        <v-icon size="64" color="grey-lighten-1">mdi-inbox-outline</v-icon>
                        <p class="text-h6 text-grey mt-4">No approved reversals found</p>
                        <p class="text-body-2 text-grey">Try adjusting your filters</p>
                    </div>

                    <!-- Reversal Cards -->
                    <v-row v-else>
                        <v-col v-for="(item, index) in paginatedItems" :key="item.id || index" cols="12" md="4" class="mb-4">
                            <v-card class="reversal-card" elevation="2" border="sm opacity-1" rounded="lg">
                                <v-card-text class="pa-4 card-content">
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-h6 font-weight-bold">{{ item.dealer_name || 'Dealer Details Pending' }}</span>
                                        <span class="text-h6 font-weight-bold purple-text">Rs. {{
                                            formatNumber(item.amount) }}</span>
                                    </div>

                                    <div class="d-flex align-center mb-2">
                                        <span class="text-body-2 text-grey-darken-1 area-invoice-text">
                                            <span class="invoice-dot">{{ item.receipt_id }}</span>
                                        </span>
                                    </div>

                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-clock-outline</v-icon>
                                            <span class="text-body-2">{{ item.time }}</span>
                                        </div>
                                    </div>

                                    <div class="d-flex justify-space-between align-center mb-3">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-calendar-outline</v-icon>
                                            <span class="text-body-2">Collection Date</span>
                                        </div>
                                        <span class="text-body-2 font-weight-medium">{{ item.collection_date }}</span>
                                    </div>

                                    <v-card class="payment-type-box pa-3 mb-3" variant="flat" elevation="0"
                                        rounded="md">
                                        <div class="d-flex align-center">
                                            <v-icon size="large" :color="getPaymentIconColor(item.payment_type)"
                                                class="mr-3">
                                                {{ getPaymentIcon(item.payment_type) }}
                                            </v-icon>
                                            <div>
                                                <div class="text-body-2 font-weight-bold">{{
                                                    getPaymentTypeText(item.payment_type) }}
                                                </div>
                                                <div class="text-caption text-grey-darken-1">Payment Status: {{ item.payment_status }}</div>
                                            </div>
                                        </div>
                                    </v-card>

                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="text-body-1 font-weight-bold">Reason for Reversal</div>
                                        <v-btn 
                                            class="more-details-btn sharp-edge" 
                                            variant="text" 
                                            size="small"
                                            @click="navigateToCollectionDetail(item)"
                                            :ripple="false"
                                        >
                                            More Details
                                        </v-btn>
                                    </div>

                                    <v-card class="reason-box pa-3 mb-2 reason-fixed-height" variant="outlined"
                                        elevation="0" border="sm opacity-1" rounded="md">
                                        <span class="text-body-2 reason-text">{{ item.reason }}</span>
                                    </v-card>

                                    <div class="d-flex justify-space-between align-center mt-2 mb-1">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="green-darken-1"
                                                class="mr-1">mdi-check-circle-outline</v-icon>
                                            <span class="text-body-2 font-weight-medium">Approved on</span>
                                        </div>
                                        <span class="text-body-2">{{ item.approved_date }}</span>
                                    </div>

                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="blue-darken-1"
                                                class="mr-1">mdi-account-check-outline</v-icon>
                                            <span class="text-body-2 font-weight-medium">Approved by</span>
                                        </div>
                                        <span class="text-body-2">{{ item.approved_by || 'Admin' }}</span>
                                    </div>
                                </v-card-text>

                                <v-btn class="view-feedback-btn fixed-height-btn" block variant="flat"
                                    @click="viewFeedback(item)">
                                    VIEW FEEDBACK
                                </v-btn>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Pagination -->
                    <div v-if="filteredReversals.length > 0" class="d-flex justify-end align-center mt-4">
                        <div class="d-flex align-center">
                            <span class="text-body-2 mr-2">Rows per page:</span>
                            <v-select v-model="itemsPerPage" :items="[3, 6, 9, 10, 12]" variant="outlined" density="compact"
                                style="width: 80px;" hide-details class="mr-4"></v-select>
                            <span class="text-body-2 mr-4">
                                {{ filteredReversals.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0 }}-{{ Math.min(currentPage * itemsPerPage, filteredReversals.length) }} of {{ filteredReversals.length }}
                            </span>
                            <v-btn icon="mdi-chevron-left" variant="text" :disabled="currentPage === 1"
                                @click="currentPage--"></v-btn>
                            <v-btn icon="mdi-chevron-right" variant="text" :disabled="currentPage === totalPages || totalPages === 0"
                                @click="currentPage++"></v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>

            <v-dialog v-model="feedbackDialog" max-width="600" persistent>
                <v-card rounded="md">
                    <div class="pa-4">
                        <v-card color="#F0F0F0" flat>
                            <template v-slot:prepend>
                                <v-card-title class="text-subtitle-1 font-weight-bold">REVIEW FEEDBACK</v-card-title>
                            </template>
                            <v-spacer></v-spacer>
                            <template v-slot:append>
                                <v-btn @click="feedbackDialog = false" icon="mdi-close" size="x-small"></v-btn>
                            </template>
                        </v-card>

                        <v-card color="white" flat class="mt-3">
                            <v-card-item>
                                <div class="text-body-2 font-weight-medium mb-1" style="color: #424242;">Sales Rep Reason</div>
                                <v-card class="pa-3 mb-4" style="background-color: #FFE5B4 !important;" elevation="0" rounded="md">
                                    <div class="text-body-2 font-weight-medium" style="color: #9C27B0;">Feedback By Sales Representative</div>
                                    <div class="text-body-2 mt-1" style="color: #424242; line-height: 1.5;">
                                        {{ selectedItem?.reversal_note || 'No reason provided.' }}
                                    </div>
                                    <div class="text-caption mt-2" style="color: #757575;">{{ selectedItem?.uploaded_time || 'N/A' }} | {{ selectedItem?.collection_date || 'N/A' }}</div>
                                </v-card>

                                <div class="text-body-2 font-weight-medium mb-1" style="color: #424242;">Area Manager Feedback</div>
                                <v-card class="pa-3 mb-4" style="background-color: #E8F5E9 !important;" elevation="0" rounded="md">
                                    <div class="text-body-2 font-weight-medium" style="color: #2E7D32;">Feedback By Area Manager</div>
                                    <div class="text-body-2 mt-1" style="color: #424242; line-height: 1.5;">
                                        {{ selectedItem?.reversal_approval_comment || 'No feedback provided.' }}
                                    </div>
                                    <div class="text-caption mt-2" style="color: #757575;">{{ selectedItem?.manager_time || 'N/A' }} | {{ selectedItem?.manager_date || 'N/A' }}</div>
                                </v-card>
                            </v-card-item>
                        </v-card>
                    </div>
                </v-card>
            </v-dialog>
        </v-container>
    </v-app>
</template>

<script>
import axios from 'axios';
import { mapActions } from "vuex";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getPermissions } from "@/plugins/permissions";

export default {
    data() {
        return {
            loading: false,
            panelOpen: ['filters'],
            
            // Filter fields
            dates: [],
            search: '',
            selectedAgent: [],
            selectedRoute: [],
            selectDistrict: [],
            selectedCollectionType: [],

            // Loading states
            salesRepsLoading: false,

            // Data options
            agentListsWithDetails: [],
            // Keep the original three defaults — merge in any extra from data later
            routeTypeOptions: [
                { text: 'Assign Route Plan', value: 'Assign Route Plan', title: 'Assign Route Plan' },
                { text: 'Direct', value: 'Direct', title: 'Direct' },
                { text: 'Own Route Plan', value: 'Own Route Plan', title: 'Own Route Plan' },
            ],
            areaLists: [],

            collectionTypeOptions: [
                { text: 'CASH', value: 'CASH', title: 'CASH' },
                { text: 'BANK', value: 'BANK', title: 'BANK' },
                { text: 'CHEQUE', value: 'CHEQUE', title: 'CHEQUE' },
            ],

            // Pagination
            currentPage: 1,
            itemsPerPage: 10,
            
            // Dialog
            feedbackDialog: false,
            selectedItem: null,

            baseURL: process.env.VUE_APP_API_BASE_URL,
            reversals: [],
            permissions: {},
            
            cancelTokenSource: null,
        }
    },

    computed: {
        filteredReversals() {
            let result = [...this.reversals];

            // Date filter (supports single date or range)
            const validDates = (this.dates || [])
                .filter(d => d)
                .map(d => new Date(d))
                .filter(d => !isNaN(d.getTime()))
                .sort((a, b) => a - b);

            if (validDates.length === 1) {
                const single = validDates[0];
                const from = new Date(single); from.setHours(0, 0, 0, 0);
                const to = new Date(single); to.setHours(23, 59, 59, 999);

                result = result.filter(item => {
                    if (!item.approved_date_raw) return false;
                    const d = new Date(item.approved_date_raw);
                    return d >= from && d <= to;
                });
            } else if (validDates.length >= 2) {
                const from = new Date(validDates[0]); from.setHours(0, 0, 0, 0);
                const to = new Date(validDates[validDates.length - 1]); to.setHours(23, 59, 59, 999);

                result = result.filter(item => {
                    if (!item.approved_date_raw) return false;
                    const d = new Date(item.approved_date_raw);
                    return d >= from && d <= to;
                });
            }

            // Area
            if (this.selectDistrict && this.selectDistrict.length > 0) {
                result = result.filter(item =>
                    this.selectDistrict.includes(item.area_code)
                );
            }

            // Sales Rep — compare by ID using enriched saleRep_id
            if (this.selectedAgent && this.selectedAgent.length > 0) {
                const selectedIds = this.selectedAgent.map(id => String(id));
                result = result.filter(item => {
                    if (item.saleRep_id === null || item.saleRep_id === undefined) return false;
                    return selectedIds.includes(String(item.saleRep_id));
                });
            }

            // Route — case + whitespace tolerant comparison
            if (this.selectedRoute && this.selectedRoute.length > 0) {
                const selectedTypes = this.selectedRoute.map(t => String(t).trim().toLowerCase());
                result = result.filter(item => {
                    if (!item.route_type) return false;
                    return selectedTypes.includes(String(item.route_type).trim().toLowerCase());
                });
            }

            // Collection Type
            if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
                result = result.filter(item =>
                    this.selectedCollectionType.includes(item.payment_method_raw)
                );
            }

            // Search
            if (this.search && this.search.trim().length > 0) {
                const q = this.search.trim().toLowerCase();
                result = result.filter(item =>
                    (item.dealer_name || '').toLowerCase().includes(q) ||
                    (item.receipt_id || '').toLowerCase().includes(q) ||
                    (item.reversal_receipt_id || '').toLowerCase().includes(q) ||
                    (item.reason || '').toLowerCase().includes(q)
                );
            }

            return result;
        },

        totalPages() {
            return Math.ceil(this.filteredReversals.length / this.itemsPerPage);
        },
        
        paginatedItems() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredReversals.slice(start, end);
        }
    },

    watch: {
        itemsPerPage() {
            this.currentPage = 1;
        },
        search: {
            handler() { this.currentPage = 1; },
            deep: true
        },
        selectedAgent: {
            handler() { this.currentPage = 1; },
            deep: true
        },
        selectedRoute: {
            handler() { this.currentPage = 1; },
            deep: true
        },
        selectDistrict: {
            handler() { this.currentPage = 1; },
            deep: true
        },
        selectedCollectionType: {
            handler() { this.currentPage = 1; },
            deep: true
        },
        dates: {
            handler() { this.currentPage = 1; },
            deep: true
        },
    },

    methods: {
        ...mapActions(['updateSharedDataCollection']),

        formatNumber(value) {
            if (isNaN(value)) return '0.00';
            return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        },

        onDateChange(val) {
            if (!val || (Array.isArray(val) && val.length === 0)) {
                this.dates = [];
            } else if (!Array.isArray(val)) {
                this.dates = [val];
            } else {
                this.dates = val.filter(d => d);
            }
        },

        populateAreaOptions() {
            const codes = (this.reversals || [])
                .map(r => r.area_code)
                .filter(code => code && String(code).trim());
            this.areaLists = [...new Set(codes)]
                .sort()
                .map(code => ({ text: code, value: code, title: code }));
        },

        // Merge hardcoded defaults with any route types actually present in the data
        populateRouteOptions() {
            const defaults = ['Assign Route Plan', 'Direct', 'Own Route Plan'];
            const fromData = (this.reversals || [])
                .map(r => r.route_type)
                .filter(t => t && String(t).trim());

            // Union of defaults + data, preserving defaults order first
            const all = [...new Set([...defaults, ...fromData])];

            this.routeTypeOptions = all.map(t => ({ text: t, value: t, title: t }));

            console.log('🛣️ Route types available:', this.routeTypeOptions.map(o => o.text));
        },

        backPageRoute() {
            this.$router.go(-1);
        },

        getPaymentIcon(paymentType) {
            switch (paymentType?.toLowerCase()) {
                case 'cash': return 'mdi-cash';
                case 'cheque': return 'mdi-file-document-outline';
                case 'bank': return 'mdi-bank';
                case 'cash + credit memo': return 'mdi-cash-multiple';
                case 'offset': return 'mdi-swap-horizontal';
                default: return 'mdi-cash';
            }
        },

        getPaymentIconColor(paymentType) {
            switch (paymentType?.toLowerCase()) {
                case 'cash': return '#4CAF50';
                case 'cheque': return '#2196F3';
                case 'bank': return '#9C27B0';
                case 'cash + credit memo': return '#FF9800';
                case 'offset': return '#F44336';
                default: return '#4CAF50';
            }
        },

        getPaymentTypeText(paymentType) {
            switch (paymentType?.toLowerCase()) {
                case 'cash': return 'Cash Payment';
                case 'cheque': return 'Cheque Payment';
                case 'bank': return 'Bank Deposit';
                case 'cash + credit memo': return 'Cash + Credit Memo';
                case 'offset': return 'Offset Payment';
                default: return 'Cash Payment';
            }
        },

        navigateToCollectionDetail(item) {
            sessionStorage.setItem('return_to_approved_reversals', 'true');
            
            if (item && item.receipt_id) {
                this.$router.push(`/total-collections-view/${item.receipt_id}`);
            } else {
                console.error('No valid receipt_id found for navigation', item);
            }
        },

        viewFeedback(item) {
            this.selectedItem = item;
            this.feedbackDialog = true;
        },

        exportPDF() {
            const doc = new jsPDF('landscape');
            doc.text('Approved Payment Reversals', 14, 10);
            const tableData = this.filteredReversals.map((item, index) => [
                index + 1,
                item.dealer_name || 'N/A',
                item.area_code || 'N/A',
                item.receipt_id,
                item.reversal_receipt_id,
                `Rs. ${this.formatNumber(item.amount)}`,
                item.collection_date,
                this.getPaymentTypeText(item.payment_type),
                item.payment_status,
                item.approved_date || 'N/A'
            ]);
            doc.autoTable({ 
                head: [['#', 'Dealer', 'Area', 'Receipt ID', 'Reversal ID', 'Amount', 'Date', 'Payment Type', 'Status', 'Approved Date']], 
                body: tableData,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [156, 39, 176] }
            });
            doc.save('approved_reversals.pdf');
        },

        exportExcel() {
            const tableData = this.filteredReversals.map((item, index) => ({
                'No': index + 1,
                'Dealer': item.dealer_name || 'N/A',
                'Area': item.area_code || 'N/A',
                'Receipt ID': item.receipt_id,
                'Reversal ID': item.reversal_receipt_id,
                'Amount': `Rs. ${this.formatNumber(item.amount)}`,
                'Collection Date': item.collection_date,
                'Payment Type': this.getPaymentTypeText(item.payment_type),
                'Payment Status': item.payment_status,
                'Reason for Reversal': item.reason,
                'Approval Comment': item.reversal_approval_comment,
                'Approved Date': item.approved_date || 'N/A',
                'Route Type': item.route_type || 'N/A'
            }));
            const worksheet = XLSX.utils.json_to_sheet(tableData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Approved Reversals');
            XLSX.writeFile(workbook, 'approved_reversals.xlsx');
        },

        async enrichWithSaleRepId(reversals) {
            const token = localStorage.getItem('token');
            const missing = reversals.filter(r => r.saleRep_id === null || r.saleRep_id === undefined);

            if (missing.length === 0) {
                console.log('✅ All reversals already have saleRep_id');
                return reversals;
            }

            console.log(`🔎 Enriching ${missing.length} reversals with saleRep_id...`);

            const detailPromises = missing.map(r =>
                axios.get(`${this.baseURL}/api/reversal-receipt/v1/${r.receipt_id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }).then(res => ({
                    receipt_id: r.receipt_id,
                    saleRep_id: res.data?.data?.saleRep_id ?? null,
                })).catch(err => {
                    console.warn(`⚠️ Failed to fetch detail for ${r.receipt_id}:`, err.message);
                    return { receipt_id: r.receipt_id, saleRep_id: null };
                })
            );

            const details = await Promise.all(detailPromises);
            const idMap = {};
            details.forEach(d => { idMap[d.receipt_id] = d.saleRep_id; });

            return reversals.map(r => {
                if ((r.saleRep_id === null || r.saleRep_id === undefined) && idMap[r.receipt_id] != null) {
                    return { ...r, saleRep_id: idMap[r.receipt_id] };
                }
                return r;
            });
        },

        async fetchSalesRepsFromAPI() {
            try {
                this.salesRepsLoading = true;
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId') || 1;
                
                const url = `${this.baseURL}/api/getSalesRepByUserAreaCodes?userId=${userId}`;

                const response = await axios.get(url, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data.success && response.data.data) {
                    this.agentListsWithDetails = response.data.data.map(rep => ({
                        text: rep.salesRep_name || rep.user_name || `Sales Rep ${rep.id}`,
                        value: rep.id,
                        registration: rep.userCode || String(rep.id),
                        id: rep.id,
                        userCode: rep.userCode,
                        email: rep.email,
                        areaCodes: rep.area_codes,
                    })).sort((a, b) => a.text.localeCompare(b.text));
                } else {
                    this.agentListsWithDetails = [];
                }
            } catch (error) {
                console.error('Error fetching sales reps:', error);
                this.agentListsWithDetails = [];
            } finally {
                this.salesRepsLoading = false;
            }
        },

        async fetchReversalsData() {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel('New request initiated');
            }
            this.cancelTokenSource = axios.CancelToken.source();
            this.loading = true;

            try {
                const token = localStorage.getItem('token');

                const response = await axios.get(`${this.baseURL}/api/reversal-receipt/v1/approved-all`, {
                    cancelToken: this.cancelTokenSource.token,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    const apiData = response.data.data;

                    let mapped = (apiData || []).map(item => {
                        const createdAt = new Date(item.created_at);
                        const updatedAt = new Date(item.updated_at);

                        return {
                            id: item.id,
                            receipt_id: item.receipt_id,
                            reversal_receipt_id: item.reversal_receipt_id,
                            dealer_name: item.dealer_name || 'Dealer Details Pending',
                            area_code: item.area_code,
                            saleRep_id: item.saleRep_id ?? null,
                            route_type: item.route_type,
                            payment_method_raw: item.payment_method,
                            payment_method: item.payment_method,
                            ar_company: item.ar_company,
                            payment_status: item.payment_status,
                            invoice_number: item.receipt_id,
                            amount: parseFloat(item.collect_total_amount || 0),
                            time: !isNaN(createdAt) ? createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A',
                            collection_date: !isNaN(createdAt) ? createdAt.toISOString().split('T')[0] : 'N/A',
                            payment_type: item.payment_method ? item.payment_method.toLowerCase() : 'cash',
                            uploaded_time: !isNaN(createdAt) ? createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A',
                            reason: item.reversal_note || 'No reversal note provided.',
                            reversal_note: item.reversal_note || 'No reason provided.',
                            reversal_approval_comment: item.reversal_approval_Comment || 'No feedback provided.',
                            approved_date_raw: item.updated_at,
                            approved_date: !isNaN(updatedAt) ? updatedAt.toISOString().split('T')[0] : 'N/A',
                            approved_by: 'Area Manager',
                            manager_time: !isNaN(updatedAt) ? updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A',
                            manager_date: !isNaN(updatedAt) ? updatedAt.toISOString().split('T')[0] : 'N/A',
                            image1: item.image1,
                            image2: item.image2,
                            overdue_approval_status: item.overdue_approval_status,
                            reversal_approval_status: item.reversal_approval_status
                        };
                    }).sort((a, b) => b.id - a.id);

                    // Enrich with saleRep_id from detail endpoint
                    mapped = await this.enrichWithSaleRepId(mapped);

                    this.reversals = mapped;

                    // Populate all filter options from loaded data
                    this.populateAreaOptions();
                    this.populateRouteOptions();
                    this.populateSalesRepOptions();

                    console.log(`✅ Loaded ${this.reversals.length} reversals, ${this.areaLists.length} areas, ${this.routeTypeOptions.length} routes`);
                } else {
                    console.warn('Unexpected API response format:', response.data);
                    this.reversals = [];
                    this.areaLists = [];
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log('⚠️ Request cancelled:', error.message);
                } else {
                    console.error('Failed to fetch reversals data:', error);
                    this.reversals = [];
                    this.areaLists = [];
                }
            } finally {
                this.loading = false;
            }
        },

        populateSalesRepOptions() {
            const apiReps = this.agentListsWithDetails || [];
            const apiIds = new Set(apiReps.map(r => String(r.value)));
            const missingIds = [...new Set(
                (this.reversals || [])
                    .map(r => r.saleRep_id)
                    .filter(id => id !== null && id !== undefined && !apiIds.has(String(id)))
            )];

            const missingReps = missingIds.map(id => ({
                text: `Sales Rep ${id}`,
                value: id,
                registration: String(id),
            }));

            this.agentListsWithDetails = [...apiReps, ...missingReps]
                .sort((a, b) => (a.text || '').localeCompare(b.text || ''));
        }
    },

    mounted() {
        const dashboardId = 10;
        this.permissions = getPermissions(dashboardId);
        
        this.fetchSalesRepsFromAPI().then(() => {
            this.fetchReversalsData();
        });
    },

    beforeUnmount() {
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('Component unmounting');
        }
    }
}
</script>

<style lang="scss" scoped>
#app_approved_reversals {
    .sharp-edge {
        border-radius: 2px !important;
    }

    .custom_font_color_view { color: #156CF7 !important; }
    .custom_card_box { font-family: var(--font-family) !important; border-top-left-radius: var(--border_top_left_radius) !important; border-top-right-radius: var(--border_top_right_radius) !important; border-bottom-right-radius: var(--border_bottom_right_radius) !important; box-shadow: 0px 4px 18px 0px #0000001F !important; }
    .custom_page_main_title { font-size: 1.8rem; font-weight: 700; line-height: 1.2; margin-bottom: 0.25rem; color: #000000; }
    .custom_page_main_sub_title { font-size: 0.95rem; opacity: 0.6; color: #000000; margin-top: 0; }
    .reversal-card { background: white !important; border: 1px solid #E0E0E0 !important; overflow: hidden; width: 100%; display: flex; flex-direction: column; &:hover { box-shadow: 0 8px 16px rgba(156, 39, 176, 0.1) !important; } }
    .card-content { flex: 1; }
    .purple-text { color: #9C27B0 !important; }
    .payment-type-box { background-color: #F5F5F5 !important; .v-icon { font-size: 30px !important; } }
    .a4-shape { border: 1px solid #E0E0E0; background-color: #FAFAFA; width: 80px !important; height: 108px !important; }
    .reason-box { background-color: #F5F5F5 !important; border: 1px solid #E0E0E0 !important; }
    .reason-fixed-height { height: 100px; overflow-y: auto; }
    
    .more-details-btn {
        color: #9C27B0 !important;
        font-size: 0.75rem !important;
        font-weight: 600 !important;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        
        &:hover {
            background-color: rgba(156, 39, 176, 0.08) !important;
        }
    }
    
    .view-feedback-btn { background: #E1BEE7 !important; color: #9C27B0 !important; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; border-radius: 0 !important; border-top: 1px solid #CE93D8 !important; width: 100%; height: 50px !important; }
    
    @media (max-width: 960px) { .v-col-md-4 { flex: 0 0 100%; max-width: 100%; } }
}
</style>